import { useState, useRef, useEffect, useCallback } from 'react';
import { PenLine, Trash2, X, RotateCcw, Check } from 'lucide-react';
import { cn } from '@/lib/utils/utils';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

/**
 * Modal fullscreen pour dessiner la signature
 */
function SignatureModal({ isOpen, onClose, onValidate, initialValue }) {
    const { t } = useTranslation();
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [isEmpty, setIsEmpty] = useState(!initialValue);
    // Canvas fixe 800x800 pour la qualité finale
    const CANVAS_SIZE = 800;
    const [displaySize, setDisplaySize] = useState(400);

    // Calculer la taille d'affichage selon le conteneur (carré)
    useEffect(() => {
        if (!isOpen) return;

        const updateSize = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                // Prendre le minimum entre largeur disponible et hauteur disponible pour un carré
                const availableWidth = rect.width - 32; // padding
                const availableHeight = rect.height - 100; // espace pour texte
                const size = Math.min(availableWidth, availableHeight, 600); // max 600px d'affichage
                setDisplaySize(Math.max(size, 250));
            }
        };

        // Petit délai pour laisser la modal s'ouvrir
        setTimeout(updateSize, 50);
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, [isOpen]);

    // Charger la valeur initiale
    useEffect(() => {
        if (!isOpen || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Effacer le canvas
        ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

        if (initialValue) {
            const img = new Image();
            img.onload = () => {
                // Dessiner l'image en conservant les proportions dans le carré
                const scale = Math.min(CANVAS_SIZE / img.width, CANVAS_SIZE / img.height);
                const x = (CANVAS_SIZE - img.width * scale) / 2;
                const y = (CANVAS_SIZE - img.height * scale) / 2;
                ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
                setIsEmpty(false);
            };
            img.src = initialValue;
        } else {
            setIsEmpty(true);
        }
    }, [isOpen, initialValue]);

    // Obtenir les coordonnées relatives au canvas
    const getCoordinates = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        if (e.touches && e.touches[0]) {
            return {
                x: (e.touches[0].clientX - rect.left) * scaleX,
                y: (e.touches[0].clientY - rect.top) * scaleY,
            };
        }

        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY,
        };
    }, []);

    // Démarrer le dessin
    const startDrawing = useCallback(
        (e) => {
            e.preventDefault();
            const canvas = canvasRef.current;
            if (!canvas) return;

            const ctx = canvas.getContext('2d');
            const { x, y } = getCoordinates(e);

            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.strokeStyle =
                getComputedStyle(document.documentElement).getPropertyValue('--foreground').trim() || '#030213';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';

            setIsDrawing(true);
            setIsEmpty(false);
        },
        [getCoordinates],
    );

    // Dessiner
    const draw = useCallback(
        (e) => {
            if (!isDrawing) return;
            e.preventDefault();

            const canvas = canvasRef.current;
            if (!canvas) return;

            const ctx = canvas.getContext('2d');
            const { x, y } = getCoordinates(e);

            ctx.lineTo(x, y);
            ctx.stroke();
        },
        [isDrawing, getCoordinates],
    );

    // Arrêter le dessin
    const stopDrawing = useCallback(() => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            ctx.closePath();
        }
        setIsDrawing(false);
    }, [isDrawing]);

    // Écouter les événements au niveau du document pour gérer le dessin en dehors du canvas
    useEffect(() => {
        if (!isOpen) return;

        const handleGlobalMouseUp = () => {
            if (isDrawing) {
                stopDrawing();
            }
        };

        // Gérer le mouvement de souris en dehors du canvas
        const handleGlobalMouseMove = (e) => {
            if (!isDrawing || !canvasRef.current) return;

            const canvas = canvasRef.current;
            const rect = canvas.getBoundingClientRect();
            const scaleX = canvas.width / rect.width;
            const scaleY = canvas.height / rect.height;

            const x = (e.clientX - rect.left) * scaleX;
            const y = (e.clientY - rect.top) * scaleY;

            const ctx = canvas.getContext('2d');
            ctx.lineTo(x, y);
            ctx.stroke();
        };

        // Gérer le mouvement tactile en dehors du canvas
        const handleGlobalTouchMove = (e) => {
            if (!isDrawing || !canvasRef.current || !e.touches[0]) return;

            const canvas = canvasRef.current;
            const rect = canvas.getBoundingClientRect();
            const scaleX = canvas.width / rect.width;
            const scaleY = canvas.height / rect.height;

            const x = (e.touches[0].clientX - rect.left) * scaleX;
            const y = (e.touches[0].clientY - rect.top) * scaleY;

            const ctx = canvas.getContext('2d');
            ctx.lineTo(x, y);
            ctx.stroke();
        };

        document.addEventListener('mouseup', handleGlobalMouseUp);
        document.addEventListener('touchend', handleGlobalMouseUp);
        document.addEventListener('mousemove', handleGlobalMouseMove);
        document.addEventListener('touchmove', handleGlobalTouchMove, { passive: true });

        return () => {
            document.removeEventListener('mouseup', handleGlobalMouseUp);
            document.removeEventListener('touchend', handleGlobalMouseUp);
            document.removeEventListener('mousemove', handleGlobalMouseMove);
            document.removeEventListener('touchmove', handleGlobalTouchMove);
        };
    }, [isOpen, isDrawing, stopDrawing]);

    // Effacer
    const handleClear = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
        setIsEmpty(true);
    }, []);

    // Valider
    const handleValidate = useCallback(() => {
        if (isEmpty) {
            onValidate(null);
        } else {
            const canvas = canvasRef.current;
            if (canvas) {
                onValidate(canvas.toDataURL('image/png'));
            }
        }
        onClose();
    }, [isEmpty, onValidate, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex flex-col bg-card">
            {/* Header */}
            <div className="flex items-center justify-between px-[20px] py-[16px] border-b border-border">
                <Button
                    variant="plain"
                    color="neutral"
                    size="auto"
                    type="button"
                    onClick={onClose}
                    className="flex items-center justify-center w-[40px] h-[40px] rounded-full hover:bg-muted transition-colors"
                >
                    <X className="w-6 h-6 text-foreground" />
                </Button>
                <h2 className="text-[18px] font-medium text-foreground">{t('common:signature')}</h2>
                <div className="w-[40px]" /> {/* Spacer */}
            </div>

            {/* Canvas Container */}
            <div ref={containerRef} className="flex-1 flex flex-col items-center justify-center p-[16px]">
                <p className="text-[14px] text-muted-foreground mb-[16px]">{t('common:signezDansLeCadreCiDessous')}</p>
                <canvas
                    ref={canvasRef}
                    width={CANVAS_SIZE}
                    height={CANVAS_SIZE}
                    className="border-2 border-dashed border-border rounded-[12px] bg-card cursor-crosshair touch-none"
                    style={{ width: displaySize, height: displaySize, aspectRatio: '1 / 1' }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                />
            </div>

            {/* Footer avec boutons */}
            <div className="px-[20px] py-[20px] border-t border-border space-y-[12px]">
                <Button
                    variant="plain"
                    color="destructive"
                    size="xxl"
                    type="button"
                    onClick={handleClear}
                    disabled={isEmpty}
                    className={cn(
                        'w-full flex items-center justify-center gap-[8px] py-[14px] rounded-[12px] border-2 border-border text-[14px] font-medium transition-colors h-auto',
                        isEmpty ? 'text-muted-foreground/50 cursor-not-allowed' : '',
                    )}
                >
                    <RotateCcw className="w-5 h-5" />
                    {t('common:effacer')}
                </Button>
                <Button
                    variant="solid"
                    color="secondary"
                    size="xxl"
                    type="button"
                    onClick={handleValidate}
                    className="w-full flex items-center justify-center gap-[8px] py-[14px] rounded-[12px] text-[14px] font-medium hover:opacity-90 transition-opacity h-auto"
                >
                    <Check className="w-5 h-5" />
                    {t('common:validerLaSignature')}
                </Button>
            </div>
        </div>
    );
}

/**
 * Composant SignatureField - Champ de signature avec aperçu et modale
 *
 * @param {Object} props
 * @param {string} props.value - Signature en base64 (ou null)
 * @param {function} props.onChange - Callback appelé avec la signature en base64 (ou null)
 * @param {string} props.label - Label du champ
 * @param {boolean} props.required - Champ obligatoire
 * @param {boolean} props.disabled - Désactive le champ
 * @param {string} props.className - Classes CSS additionnelles
 */
export function SignatureField({ value, onChange, label, required = false, disabled = false, className }) {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleOpenModal = () => {
        if (!disabled) {
            setIsModalOpen(true);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleValidate = (signatureData) => {
        if (onChange) {
            onChange(signatureData);
        }
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        if (onChange) {
            onChange(null);
        }
    };

    const hasSignature = !!value;

    return (
        <div className={className}>
            {/* Label */}
            {label && (
                <label data-required={required || undefined} className="text-[13px] text-foreground mb-[8px] block">
                    {label}
                </label>
            )}

            {/* Champ principal */}
            <div
                className={cn(
                    'relative bg-card rounded-[12px] border-2 overflow-hidden transition-colors',
                    disabled
                        ? 'border-border bg-muted cursor-not-allowed'
                        : hasSignature
                          ? 'border-secondary'
                          : 'border-border hover:border-secondary/50 cursor-pointer',
                )}
                onClick={!disabled && !hasSignature ? handleOpenModal : undefined}
            >
                {hasSignature ? (
                    /* Aperçu de la signature - carré */
                    <div className="p-[16px]">
                        <div className="flex items-start gap-[16px]">
                            {/* Image de la signature - carré aspect-ratio 1:1 */}
                            <div
                                className="flex-1 flex items-center justify-center bg-muted rounded-[12px] overflow-hidden"
                                style={{ aspectRatio: '1 / 1', maxHeight: '400px' }}
                            >
                                <img src={value} alt={t('common:signature')} className="w-full h-full object-contain p-[16px]" />
                            </div>

                            {/* Boutons d'action */}
                            {!disabled && (
                                <div className="flex flex-col gap-[8px]">
                                    <Button
                                        variant="plain"
                                        color="neutral"
                                        size="auto"
                                        type="button"
                                        onClick={handleOpenModal}
                                        className="flex items-center justify-center w-[40px] h-[40px] rounded-[10px] bg-muted hover:bg-border transition-colors"
                                        title={t('common:modifierLaSignature')}
                                    >
                                        <PenLine className="w-5 h-5 text-muted-foreground" />
                                    </Button>
                                    <Button
                                        variant="plain"
                                        color="destructive"
                                        size="auto"
                                        type="button"
                                        onClick={handleDelete}
                                        className="flex items-center justify-center w-[40px] h-[40px] rounded-[10px] bg-destructive/10 hover:bg-destructive/20 transition-colors"
                                        title={t('common:supprimerLaSignature')}
                                    >
                                        <Trash2 className="w-5 h-5 text-destructive" />
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Badge signé */}
                        <div className="flex items-center gap-[6px] mt-[12px]">
                            <Check className="w-4 h-4 text-secondary" />
                            <span className="text-[12px] text-secondary font-medium">{t('common:signe')}</span>
                        </div>
                    </div>
                ) : (
                    /* État vide - bouton pour ouvrir la modale */
                    <Button
                        variant="plain"
                        color="neutral"
                        size="cta"
                        type="button"
                        onClick={handleOpenModal}
                        disabled={disabled}
                        className={cn(
                            'w-full p-[24px] flex flex-col items-center justify-center gap-[12px] h-auto',
                            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                        )}
                    >
                        <div className="w-[56px] h-[56px] rounded-full bg-secondary/10 flex items-center justify-center">
                            <PenLine className="w-6 h-6 text-secondary" />
                        </div>
                        <span className="text-[14px] text-muted-foreground">{t('common:appuyezPourSigner')}</span>
                    </Button>
                )}
            </div>

            {/* Modal de signature */}
            <SignatureModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onValidate={handleValidate}
                initialValue={value}
            />
        </div>
    );
}
